<template>
  <IntercomInput
    :model-value="displayValue"
    type="tel"
    inputmode="numeric"
    maxlength="10"
    :valid="isValid"
    :hint="showHint ? formatHint : undefined"
    :error="error"
    :label="label"
    :placeholder="placeholder || '98765 43210'"
    :required="required"
    :disabled="disabled"
    v-bind="$attrs"
    @update:model-value="handleInput"
    @blur="handleBlur"
  >
    <template #prefix>
      <span class="text-neutral-500 font-medium">+91</span>
    </template>
  </IntercomInput>
</template>

<script setup>
/**
 * IntercomPhoneInput - E.164 compatible phone input for Indian numbers
 *
 * Storage format: E.164 (+919876543210)
 * Display format: +91 | 9876543210 (prefix is separate, user types 10 digits)
 *
 * Accepts both formats as input:
 * - "9876543210" (legacy 10-digit)
 * - "+919876543210" (E.164)
 *
 * Always emits E.164 format when valid, raw digits otherwise.
 */
import { ref, computed, watch } from 'vue'
import IntercomInput from './IntercomInput.vue'

defineOptions({
  inheritAttrs: false,
})

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  error: {
    type: String,
    default: '',
  },
  required: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  showHint: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['update:modelValue', 'blur', 'update:valid'])

// Internal state: store 10 digits for display
const localDigits = ref('')

/**
 * Parse E.164 or other formats to 10-digit display value
 * +919876543210 → 9876543210
 * 919876543210 → 9876543210
 * 9876543210 → 9876543210
 */
function parseFromE164(value) {
  if (!value) return ''

  // Strip +91 prefix if present
  if (value.startsWith('+91') && value.length === 13) {
    return value.slice(3)
  }

  // Strip 91 prefix if 12 digits
  if (value.startsWith('91') && value.length === 12) {
    return value.slice(2)
  }

  // Return just digits (handles any other format)
  return value.replace(/\D/g, '').slice(0, 10)
}

/**
 * Format 10 digits to E.164
 * 9876543210 → +919876543210
 */
function formatToE164(digits) {
  if (!digits || digits.length !== 10) return digits
  return '+91' + digits
}

// Initialize from prop
watch(
  () => props.modelValue,
  (newValue) => {
    const parsed = parseFromE164(newValue || '')
    // Only update if different to avoid cursor jumping
    if (parsed !== localDigits.value) {
      localDigits.value = parsed
    }
  },
  { immediate: true }
)

// Display value for the input (10 digits)
const displayValue = computed(() => localDigits.value)

// Hint shows progress
const formatHint = computed(() => {
  const len = localDigits.value.length
  if (len === 0) return ''
  if (len < 10) return `${len}/10`
  return 'Valid'
})

// Valid when exactly 10 digits
const isValid = computed(() => {
  return localDigits.value.length === 10 && /^\d{10}$/.test(localDigits.value)
})

// Emit validity changes
watch(
  isValid,
  (valid) => {
    emit('update:valid', valid)
  },
  { immediate: true }
)

const handleInput = (value) => {
  // Filter to only digits, max 10
  const digitsOnly = value.replace(/\D/g, '').slice(0, 10)
  localDigits.value = digitsOnly

  // Emit E.164 format if valid, otherwise raw digits
  const emitValue = digitsOnly.length === 10 ? formatToE164(digitsOnly) : digitsOnly
  emit('update:modelValue', emitValue)
}

const handleBlur = (event) => {
  emit('blur', event)
}
</script>
